import _ from 'lodash';
import filters from '../core/filters';

import infoBoard from '../core/infoboard';
import worldMap from '../core/map';
import translate from '../core/tools/translate';
import environment from '../environment';
import { CoreControl } from './CoreControl';
import { VectorType } from './VectorType';
import api from '../api';
import { FeaturePermission } from '../../constants/feature.permission.helper';
import { ContextEnum } from '../../enuns/context.enum';
import { BROWNFIELD_Z_INDEX_OFFSET } from '../layers/BrownfieldLayer';
import layers from '../layers';

const LayerInfo = (name) => require('../info').get(name);
const LayerControls = (name) => require('./index').get(name);

export class BrownfieldControl extends CoreControl {
    constructor () {
        super({ name: 'Brownfield' });

        this._filterCallback = this._filterCallback.bind(this);
        this._changePeriod = this._changePeriod.bind(this);
        this._toggleLabel = this._toggleLabel.bind(this);
        this.createFilter = this.createFilter.bind(this);
        this.resizeIcon = this.resizeIcon.bind(this);
        this._change = this._change.bind(this);
        this.highlight = this.highlight.bind(this);
        this.unselect = this.unselect.bind(this);
        this.select = this.select.bind(this);
        this.stackFeatures = this.stackFeatures.bind(this);
        this.stack = this.stack.bind(this);
    }

    static getInstance () {
        if (!BrownfieldControl._instance) {
            BrownfieldControl._instance = new BrownfieldControl();
        }

        return BrownfieldControl._instance;
    }

    init () {
        this._tags = {
            tagName: {
                property: 'PointName',
                align: 'east',
                size: 9,
                bold: true,
                box: true
            },
            tagAreaName: {
                property: 'Nome',
                box: true,
                colorize: true,
                color: '#fff59d',
                bold: true,
                size: 9,
                offsetY: 60
            },
            tagAction: {
                property: 'ActionName',
                offsetY: -21,
                size: 10,
                colorize: true,
                color: '#ffcc80',
                bold: true,
                box: true,
            },
            tagGdsi: {
                property: 'GdsiTag',
                size: 10,
                colorize: true,
                color: 'GdsiColor',
                type: 'property',
                bold: true,
                box: true,
            },
            tagShowTypePoint: {
                property: 'PointTypeTag',
                align: 'south',
                size: 10,
                colorize: true,
                color: 'PointTypeColor',
                type: 'property',
                bold: true,
                box: true,
            }
        };

        const values = {
            disableSlider: true,
            help: translate(`Help.${this.options.name}`),
            id: 'layer-brownfield',
            input: 'checkbox',
            label: translate(`Common.${this.options.name}`),
            layer: this.getLayer,
            change: this._change,
            name: this.options.name,
            type: 'vector-points',
            filter: api.authorization.isAuthorized(FeaturePermission.LAYER_BROWNFIELD.filterType)
                ? { enable: true, click: this.createFilter }
                : undefined,
            purge: this.purge,
            tab () {
                return $('#brownfield-tab-link');
            },
            resize: {
                name: `pinSize${this.options.name}`,
                change: this.resizeIcon,
                type: 'radio'
            },
            dropdown: [],
            checkbox: this._getCheckboxOptions()
        };

        values.dropdown.push({
            name: 'year',
            dataSource: _.map(environment.get('CONFIG.FILTERS.BROWNFIELD_SCENARIO_YEARS'), (val) => ({ text: val, value: val })),
            index: _.indexOf(environment.get('CONFIG.FILTERS.BROWNFIELD_SCENARIO_YEARS'), this.getLayer().options.remote.params.year),
            change: this._changePeriod
        });

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    _change (e) {
        try {
            api.general.log.action(environment._getContextEnum(this.options.name), e.checked ? 'enable' : 'disable');
            const layer = this.getLayer();

            if (e.checked) {
                layer.redraw();
            } else {
                layer.hide();
            }
        } catch (err) {
            console.log('ERROR: ', err);
        }
    }

    createFilter () {
        return filters.create({
            id: `filter-brownfield`,
            label: translate(`Common.Brownfield`),
            type: 'brownfield',
            direction: 'up',
            onSubmit: this._filterCallback
        });
    }

    _getCheckboxOptions () {
        const contents = [
            { name: 'tagName', label: translate('Action.ShowTagPointName') },
            { name: 'tagAreaName', label: translate('Action.ShowTagAreaName') },
            { name: 'tagAction', label: translate('Brownfield.ShowAction') },
            { name: 'tagShowTypePoint', label: translate('Greenfield.ShowPointType') },
            { name: 'tagGdsi', label: 'GDSI', help: translate('Brownfield.GdsiHelp') },
        ];

        const res = {};
        contents.forEach(content => {
            res[content.label] = {
                name: content.name,
                help: content.help,
                change: isChecked => {
                    this._toggleLabel(isChecked, content.name);
                },
                style: {
                    change: tag => {
                        this._tags[content.name] = tag;
                        this._toggleLabel();
                    },
                    options: this._tags[content.name]
                }
            };
        });

        return res;
    }

    _getLayerTag () {
        if (!this.getControls().checkbox) { return undefined; }

        const tags = [];

        if ((this.getControls().checkbox.tagName || {}).status) {
            tags.push(_.assign({}, this._tags.tagName));
        }

        if ((this.getControls().checkbox.tagAction || {}).status) {
            tags.push(_.assign({}, this._tags.tagAction));
        }

        if ((this.getControls().checkbox.tagGdsi || {}).status) {
            tags.push(_.assign({}, this._tags.tagGdsi));
        }

        if ((this.getControls().checkbox.tagShowTypePoint || {}).status) {
            tags.push(_.assign({}, this._tags.tagShowTypePoint));
        }

        _.forEach(_.reject(tags, tag => tag.align === 'south'), (tag, index) => {
            tag.stackedRow = index;
        });

        return tags.length === 1 ? tags[0] : tags;
    }

    _getLayerAreaTag () {
        if (!this.getControls().checkbox) { return undefined; }

        const {
          tagAreaName,
        } = this.getControls().checkbox;
        const tags = [];

        if ((tagAreaName || {}).status) {
            tags.push(_.assign({}, this._tags.tagAreaName));
        }

        return tags.length === 1 ? tags[0] : tags;
    }

    _setLabel () {
        this.getLayer().setOptions({ tag: this._getLayerTag() });
        this.getLayer(worldMap.getCurrent(), 'BrownfieldArea').setOptions({ tag: this._getLayerAreaTag() });
    }

    _toggleLabel (isChecked, name) {
        api.general.log.action(ContextEnum.LayerBrownfield, `${isChecked ? 'Show' : 'Hide'} ${name}`);
        this._setLabel();
        if (this.getLayer().visible()) {
            this.getLayer().reload();
            this.getLayer(worldMap.getCurrent(), 'BrownfieldArea').reload();
        }

        try {
            LayerControls('NewGreenfield').stack();
        } catch (error) {
            console.log('Greenfield Layer not available');
        }
    }

    _changePeriod (year) {
        this.getLayer().options.remote.params.year = String(year);

        if (this.check() && this.getLayer().visible()) {
            api.general.log.action(ContextEnum.LayerBrownfield, 'Update Filter Brownfield Year', JSON.stringify({ Year: String(year) }));
            this.getLayer().redraw();
        }
    }

    _filterCallback (values) {
        const layerFilers = {
            IdAction: values.IdAction ? (_.isArray(values.IdAction.value) && _.size(values.IdAction.value)) ? values.IdAction.value : [] : [],
            IdPointType: values.IdPointType ? (_.isArray(values.IdPointType.value) && _.size(values.IdPointType.value)) ? values.IdPointType.value : [] : []
        };

        const filter = this.getLayerFilters();

        _.forEach(layerFilers, (val, key) => {
            if (_.size(val) || (_.isBoolean(val))) {
                filter.properties[key] = val;
            } else {
                delete filter.properties[key];
            }
        });

        this.setLayerFilters(filter);
        api.general.log.action(ContextEnum.LayerBrownfield, 'Filtro', JSON.stringify(filter));

        if (!this.check()) {
            this.check(true);
        }
    }

    getLayerFilters() {
        const filter = (this.getLayer().options.filter || {});

        if (!filter.properties) filter.properties = {};

        return filter;
    }

    setLayerFilters (filter) {
        this.getLayer().setOptions({ filter });

        if (this.check() && this.getLayer().visible()) {
            this.getLayer().reload();
        }
    }

    toggleVisibility (id, layer) {
        return this._instance.toggleVisibility(id, layer);
    }

    highlight (marker, event) {
        return this._instance.highlight(marker, event);
    }

    select(marker, type) {
        this._instance.select(marker, type);
    }

    unselect(marker, type) {
        this._instance.unselect(marker, type);
    }

    view (values) {
        return LayerInfo(this.options.name).create(values);
    }

    show (options = {}, features = this.getLayer().getCurrentFeatures()) {
        if ($('#brownfield-collapse').length === 0 || options.refresh) {
            if (options.empty) {
                this.view(null);
            } else {
                this.view(features);
            }
        }

        if (options.show) {
            infoBoard.open();
            $('#brownfield-tab-link').trigger('click');
            if (options.id) {
                const title = $(`#${this.options.name.toLocaleLowerCase()}-title-${options.id}`);
                return setTimeout(() => {
                    if (title[0] && !title[0].isOpen) {
                        title.trigger('select');
                    }
                }, 100);
            }
        }
    }

    stackFeatures (features, currentMap = worldMap.getCurrent()) {
        const stores = _.flatMap(['Dealerships', 'StoresConsortium'], name => {
            const network = (layers.getAll(currentMap._id) || {})[name];
            return network && network.visible() && network.layer ? network.layer.getLayers().filter(l => l.feature) : [];
        });
        const storesById = _.keyBy(stores, s => s.feature.properties.Id);

        _.forEach(features, f => {
            const store = storesById[f.properties.IdCurrentStore];
            const isSamePlace = store && _.every([0, 1], i => Math.abs(store.feature.geometry.coordinates[i] - f.geometry.coordinates[i]) < 0.000001);

            f.properties['Icon'] = isSamePlace ? 'action_stacked' : 'action';
        });
    }

    stack (currentMap = worldMap.getCurrent()) {
        if (!this.check() || !this.getLayer(currentMap).visible()) {
            return;
        }

        this.stackFeatures(this.getLayer(currentMap).getFeatures(), currentMap);
        this.getLayer(currentMap).reload();
    }

    resizeIcon (value) {
        const { symbology } = this.getLayer().options;
        switch (value) {
        case 'large':
            symbology.values = [{
                value: 'action',
                style: {
                    icon: {
                        fields: ['ActionIcon'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{ActionIcon}.png`,
                        iconSize: [45, 58], // size of the icon
                        iconAnchor: [22, 55], // point of the icon which will correspond to marker's location
                        labelAnchor: [22, -29]
                    },
                    zIndexOffset: BROWNFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'action_stacked',
                style: {
                    icon: {
                        fields: ['ActionIcon'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{ActionIcon}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -6],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: BROWNFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }];
            break;

        case 'medium':
            symbology.values = [{
                value: 'action',
                style: {
                    icon: {
                        fields: ['ActionIcon'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{ActionIcon}_sm.png`,
                        iconSize: [35, 45], // size of the icon
                        iconAnchor: [18, 45], // point of the icon which will correspond to marker's location
                        labelAnchor: [18, -24]
                    },
                    zIndexOffset: BROWNFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'action_stacked',
                style: {
                    icon: {
                        fields: ['ActionIcon'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{ActionIcon}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -6],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: BROWNFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }];
            break;

        case 'small':
            symbology.values = [{
                value: 'action',
                style: {
                    icon: {
                        fields: ['ActionIcon'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{ActionIcon}_xs.png`,
                        iconSize: [26, 26], // size of the icon
                        iconAnchor: [13, 13], // point of the icon which will correspond to marker's location
                        labelAnchor: [6, 0]
                    },
                    zIndexOffset: BROWNFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'action_stacked',
                style: {
                    icon: {
                        fields: ['ActionIcon'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{ActionIcon}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -6],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: BROWNFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }];
            break;
        }

        this.getLayer().setOptions({ symbology });
        if (this.check() && this.getLayer().visible()) {
            return this.getLayer().reload();
        }
    }
}

export default BrownfieldControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/BrownfieldControl.js